import { defaultInvitationFilters, type InvitationFilters } from "./identity-models";
import type { AuditTrailFilters, PartnerFilters, UserFilters } from "./models";
import { queryKeys } from "./query-keys";
import { adminRepository } from "./repository";

interface QueryContext {
  signal: AbortSignal;
}

export const defaultUserFilters = {
  audience: "TRAVELLERS",
  page: 1,
  pageSize: 10,
  search: "",
  sort: "name",
  sortDirection: "asc",
  state: "ALL",
} satisfies UserFilters;

export const defaultAdminFilters = {
  ...defaultUserFilters,
  audience: "TEAM",
} satisfies UserFilters;

export const defaultAuditTrailFilters = {
  actorType: "ALL",
  category: "ALL",
  includeSystem: false,
  outcome: "ALL",
  page: 1,
  pageSize: 10,
  search: "",
} satisfies AuditTrailFilters;

export const defaultPartnerFilters = {
  lifecycleStatus: "ALL",
  outreachStatus: "ALL",
  page: 1,
  pageSize: 10,
  search: "",
} satisfies PartnerFilters;

export function usersQueryOptions(filters: UserFilters) {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listUsers(filters, signal),
    queryKey: queryKeys.users(filters),
  };
}

export function partnersQueryOptions(filters: PartnerFilters) {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listPartners(filters, signal),
    queryKey: queryKeys.partners(filters),
  };
}

export function partnerTeamQueryOptions(partnerId: string) {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listPartnerTeam(partnerId, signal),
    queryKey: queryKeys.partnerTeam(partnerId),
  };
}

export function administratorsQueryOptions() {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listAdministrators(signal),
    queryKey: queryKeys.administrators,
  };
}

export function invitationsQueryOptions(filters: InvitationFilters = defaultInvitationFilters) {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listInvitations(filters, signal),
    queryKey: queryKeys.invitationList(filters),
  };
}

export function rolesQueryOptions() {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listRoles(signal),
    queryKey: queryKeys.roles,
    staleTime: 10 * 60_000,
  };
}

export function legalDocumentsQueryOptions() {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listLegalDocuments(signal),
    queryKey: queryKeys.legal,
    staleTime: 5 * 60_000,
  };
}

export function auditTrailQueryOptions(filters: AuditTrailFilters) {
  return {
    queryFn: ({ signal }: QueryContext) => adminRepository.listAuditTrail(filters, signal),
    queryKey: queryKeys.auditTrail(filters),
    // Audit pages are intentionally demand-driven. A page is fetched on first entry,
    // pagination/filter input, or the explicit Refresh action; mounting an already
    // cached page must not create another server scan in the background.
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  };
}

export function auditTrailDetailQueryOptions(interactionId: string) {
  return {
    queryFn: ({ signal }: QueryContext) =>
      adminRepository.getAuditTrailDetail(interactionId, signal),
    queryKey: queryKeys.auditTrailDetail(interactionId),
    // Audit evidence is read only after the operator opens this exact entry. Reusing
    // the cached entry must not scan it again on remount or reconnect.
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  };
}
